import type { OperationRun } from '@foodlista/trigger-vue'
import type { ScopedOperationScope } from '../utils/scoped-operation-contract'
import {
  type MenuTranslateInput,
  menuTranslateManifest,
  type MenuTranslateOutput,
  menuTranslateOutputSchema,
  type MenuTranslateProgressPhase,
} from '@foodlista/operations'
import { PRODUCTS_CACHE_KEYS } from '~/domains/products/queries'
import { RESTAURANTS_CACHE_KEYS } from '~/domains/restaurants/queries'
import { defineScopedOperation } from '../composables/defineScopedOperation'
import { presentMenuTranslate } from '../presentation/presenters'
import { registerOperationPresenter } from '../presentation/registry'
import { registerOperationProgressTranslator } from '../progress/registry'
import { menuTranslateScopePayload } from '../scope/payloads'

export type MenuTranslationInput = MenuTranslateInput

export interface MenuTranslationScope extends ScopedOperationScope {
  flowId: string
}

export function parseMenuTranslateOutput(
  run: OperationRun,
): MenuTranslateOutput {
  return menuTranslateOutputSchema.parse(run.output)
}

registerOperationProgressTranslator(
  menuTranslateManifest.type,
  (phase, t) => {
    const typed = phase as MenuTranslateProgressPhase
    switch (typed.kind) {
      case 'translating':
        return t('operations.boc.progress.menuTranslate.translating')
      case 'verifying':
        return t('operations.boc.progress.menuTranslate.verifying')
      case 'fallback':
        return t('operations.boc.progress.menuTranslate.fallback')
      case 'done':
        return t('operations.boc.progress.menuTranslate.done')
    }
  },
)

registerOperationPresenter(menuTranslateManifest.type, presentMenuTranslate)

export const useMenuTranslateOperation = defineScopedOperation<
  MenuTranslationInput,
  MenuTranslationScope,
  MenuTranslateOutput
>({
  manifest: menuTranslateManifest,
  idempotency: {
    mode: 'scope',
    keyFromScope: scope => scope.flowId,
  },
  scopePayload: menuTranslateScopePayload,
  parseOutput: parseMenuTranslateOutput,
  enqueue: (input, { orpc, buildEnqueueInput }) =>
    orpc.ai.translateMenuAsync(buildEnqueueInput(input)),
  // The run persists the translations server-side (by id). Once it lands we
  // just refresh the caches so the (already-created) menu/products re-render
  // with their localized content — this survives modal close / page refresh.
  onCompleted: async ({ scope, queryClient }) => {
    if (!scope.restaurantSlug) {
      return
    }

    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: RESTAURANTS_CACHE_KEYS.bySlug(scope.restaurantSlug),
      }),
      queryClient.invalidateQueries({
        queryKey: PRODUCTS_CACHE_KEYS.bySearch.all(scope.restaurantSlug),
      }),
    ])
  },
})
